import { api, ApiError } from './client'
import { etatDeLaFile, mettreEnFile, vider } from '@/lib/hors-ligne/file'

/**
 * Le Fil et le journal des réponses — 02 §F2, 05 §7.2.
 *
 * Un seul point d'envoi pour les réponses, en ligne comme hors ligne : c'est
 * le même journal (D-15), donc le même appel.
 */

export interface CarteOption {
  id: number
  content_md: string
}

/**
 * Une réponse de la colonne DROITE d'un appariement — [DECISIONS §150].
 *
 * Elle ne porte PAS d'identifiant d'option, mais un jeton opaque : une paire
 * est une ligne, gauche et droite partagent le même `form_options.id`, et
 * servir les deux colonnes sous cet identifiant livrerait la réponse à qui
 * ouvre les outils de développement — y compris pendant une épreuve notée, qui
 * ne reçoit justement aucune clé.
 */
export interface CarteAppariement {
  jeton: string
  content_md: string
}

/**
 * Ce que le candidat a composé — §150.
 *
 * Un seul objet pour tous les formats, parce qu'un seul chemin les envoie. Les
 * identifiants suffisaient tant que répondre voulait dire « cocher » ; un
 * appariement associe deux colonnes, et une liste plate ne peut pas le dire.
 */
export interface ReponseComposee {
  /**
   * Les options retenues. **L'ORDRE compte** pour un ordonnancement : c'est
   * lui la réponse. Pour un appariement, c'est la colonne gauche telle qu'elle
   * a été servie.
   */
  options: number[]
  /** Option de gauche vers jeton de droite — appariement seulement. */
  paires?: Record<string, string>
}

/**
 * La forme que `submitted` prend sur le réseau.
 *
 * RG-3.11septies — **la LISTE NUE** pour tous les formats qui se cochent ou se
 * rangent : le correcteur normalise une liste ou `{options: […]}`, et
 * l'envelopper autrement lui fait corriger une réponse vide. L'appariement,
 * lui, envoie `{paires: {…}}` — c'est la seule forme qui porte deux colonnes.
 */
export function enSoumission(reponse: ReponseComposee): unknown {
  return reponse.paires === undefined ? reponse.options : { paires: reponse.paires }
}

export interface Carte {
  item_id: string
  form_version_id: number
  type: string
  stem_md: string
  options: CarteOption[]
  /**
   * La colonne DROITE d'un appariement — §150. Absente de tout autre format.
   *
   * Les mêmes paires que `options`, dans un ORDRE différent : c'est ce second
   * mélange qui fait la question. S'il suivait celui de gauche, apparier ligne
   * à ligne suffirait.
   */
  appariements?: CarteAppariement[]
  tag: { path: string; label: string }
  priority: string
  has_math: boolean
  has_table: boolean
  provenance: { label: string; year: number; session: string } | null
  /**
   * Les options justes — présentes dans le Fil, ABSENTES des évaluations
   * notées (DECISIONS du 7 août 2026, §4).
   *
   * `undefined` ne veut pas dire « aucune bonne réponse » : il veut dire « on
   * ne vous le dit pas ». Une carte de quiz de chapitre arrive ainsi, et
   * n'affiche alors aucun verdict avant la fin de l'épreuve (RG-3.11quater).
   */
  key_option_ids?: number[]
  /**
   * La séquence attendue d'un ordonnancement — §150.
   *
   * Mêmes destinataires que `key_option_ids`, et pour la même raison : le Fil
   * corrige tout de suite, une évaluation notée ne corrige pas.
   *
   * **Sans elle, un ordonnancement répondu hors connexion n'obtenait aucun
   * verdict, jamais** : l'écran ne savait pas corriger — les clés ne disent
   * pas dans quel ORDRE — et le serveur était injoignable.
   */
  key_order?: number[]
  /** L'appariement attendu — option gauche vers jeton droit, §150. */
  key_pairs?: Record<string, string>
  /**
   * **Combien de réponses sont attendues** — RG-2.2ter.
   *
   * Présent sur TOUTES les cartes à choix multiple, y compris celles d'une
   * épreuve notée : dire « deux réponses » ne dit pas *lesquelles*. C'est ce
   * qui permet d'obéir en même temps à RG-2.2bis — annoncer le compte — et à
   * RG-3.11quater — ne rien révéler pendant l'épreuve.
   *
   * Absent des formats qui ne se comptent pas : vrai/faux, réponse courte.
   */
  nb_cles?: number
  /**
   * Ce que la question a produit, tous candidats confondus — RG-2.42.
   *
   * Absent d'une carte d'évaluation notée : pendant une épreuve, savoir que
   * 41 % des candidats se trompent est un indice.
   */
  stats?: StatsDeLaQuestion
  /**
   * La correction, embarquée — elle ne s'affiche qu'APRÈS la réponse.
   *
   * Elle voyage avant pour une seule raison : l'aller-retour vers `/responses`
   * prenait plusieurs secondes sur une connexion haïtienne, et c'est ce
   * moment-là qui enseigne. Absente d'une carte d'évaluation notée.
   */
  correction?: { note_md: string | null; explication_md: string | null }
  /** Est-elle dans MES marque-pages ? Vient du serveur, pas d'un état local. */
  marquee?: boolean
}

export interface StatsDeLaQuestion {
  repondue: number
  /** Entre 0 et 1, ou `null` tant que personne n'a répondu. */
  reussite: number | null
  marquee: number
  /** Dans les trois points : intéresse qui signale, pas qui révise. */
  vue: number
  partagee: number
}

export interface PageDuFil {
  data: Carte[]
  meta: { page: number; next_page: number | null; exhausted: boolean; exam_soon: boolean }
}

export interface Devoilement {
  is_correct: boolean
  credit: number
  key_option_ids: number[]
  correction_note_md: string | null
  explanation_md: string | null
  rationales: Record<string, string>
  expected_answer: string | null
}

export interface ResultatDeSynchronisation {
  accepted: string[]
  rejected: Record<string, string>
  states: Record<string, { repetitions: number; interval_days: number; due_at: string | null }>
  reveals: Record<string, Devoilement>
}

/** RG-2.20, RG-2.23 — trois listes, un seul écran. */
export type ModeDuFil = 'feed' | 'bookmarks' | 'review'

/**
 * @param session la graine du tirage — §259. Renouvelée à chaque
 *   rafraîchissement, gardée pour toutes les pages d'une même consultation :
 *   c'est ce qui rend le fil neuf quand on le rafraîchit sans casser la
 *   pagination, qui se fait par décalage dans une permutation unique (§250).
 */
export function pageDuFil(
  page: number,
  mode: ModeDuFil = 'feed',
  session?: string,
): Promise<PageDuFil> {
  const graine = session === undefined ? '' : `&session=${encodeURIComponent(session)}`

  return api<PageDuFil>(`/feed?page=${String(page)}&mode=${mode}${graine}`)
}

/**
 * Les sept motifs de signalement — RG-04-52.
 *
 * Liste FERMÉE, et l'ordre compte : les motifs de fond d'abord, parce que ce
 * sont eux qui dépublient et qu'un candidat pressé choisit le premier qui
 * ressemble à son problème.
 */
export const MOTIFS = [
  { value: 'mauvaise_reponse', label: 'La bonne réponse indiquée est fausse' },
  { value: 'plusieurs_reponses_justes', label: 'Plusieurs réponses sont justes' },
  { value: 'explication_fausse', label: 'L’explication est fausse' },
  { value: 'enonce_ambigu', label: 'L’énoncé est ambigu' },
  { value: 'faute_langue', label: 'Faute de langue' },
  { value: 'hors_programme', label: 'Hors programme' },
  { value: 'mauvais_tag', label: 'Mal classé' },
] as const

export type MotifDeSignalement = (typeof MOTIFS)[number]['value']

export function marquer(itemId: string, marque: boolean): Promise<{ bookmarked: boolean }> {
  return api<{ bookmarked: boolean }>(`/items/${itemId}/bookmark`, {
    method: marque ? 'PUT' : 'DELETE',
  })
}

export function signaler(
  formVersionId: number,
  motif: MotifDeSignalement,
): Promise<{ reported: boolean; unpublished: boolean }> {
  return api<{ reported: boolean; unpublished: boolean }>(
    `/forms/${String(formVersionId)}/report`,
    { method: 'POST', body: JSON.stringify({ reason: motif }) },
  )
}

/** RG-2.26 — quatre longueurs, six rythmes. */
/**
 * RG-2.27 amendée — les réglages du quiz par tag ([DECISIONS §44]).
 *
 * **Vingt questions au plus.** Un quiz par tag n'est pas un examen : il ne
 * débloque rien (RG-2.30) et sert à se tester sur un thème en une pause.
 * Au-delà, il devient une séance qu'on n'achève pas — et un quiz abandonné ne
 * rend aucun score, donc n'apprend rien. Vingt questions à trente secondes font
 * dix minutes, ce qui tient dans un trajet.
 */
export const LONGUEURS = [10, 15, 20] as const

/**
 * **Dix secondes est le tempo réel d'un QCM de concours** sur une question
 * qu'on sait. L'offrir permet de travailler la vitesse séparément du fond.
 */
export const RYTHMES = [10, 20, 30, 45, 60] as const

export function lancerQuiz(
  tag: string,
  questions: number,
  secondesParQuestion: number,
): Promise<PageDuFil> {
  return api<PageDuFil>('/quiz', {
    method: 'POST',
    body: JSON.stringify({
      tag,
      questions,
      seconds_per_question: secondesParQuestion,
    }),
  })
}

export interface ReponseAEnvoyer {
  client_uuid: string
  form_version_id: number
  shown_option_ids: number[]
  submitted: unknown
  answered_at: string
  time_spent_ms: number | null
}

/**
 * Envoie un lot de réponses — ou le met en file si le réseau n'est pas là.
 *
 * **RG-9.4, [DECISIONS §111].** L'appelant n'a rien à savoir du hors-ligne :
 * c'est ici que la bascule se fait, une fois, pour le Fil comme pour les défis
 * et les quiz. La lui laisser aurait demandé la même précaution dans quatre
 * écrans, et le premier oublié aurait perdu des réponses sans qu'on le sache.
 *
 * **Le résultat rendu est alors VIDE de dévoilement** : `reveals` est vide et
 * `accepted` porte les identifiants mis en file. C'est exact — la réponse est
 * enregistrée, la correction ne l'est pas — et l'écran s'en sert pour le dire
 * plutôt que d'afficher un verdict qu'il n'a pas.
 *
 * **Seule une panne de RÉSEAU met en file.** Une erreur 4xx est un refus du
 * serveur : la réponse ne passera pas davantage demain, et la mettre en file la
 * ferait renvoyer indéfiniment.
 */
export async function envoyerReponses(
  lot: ReponseAEnvoyer[],
  contexte = 'feed',
): Promise<ResultatDeSynchronisation> {
  try {
    const resultat = await api<ResultatDeSynchronisation>('/responses', {
      method: 'POST',
      body: JSON.stringify({ context: contexte, responses: lot }),
    })

    // La voie est libre : ce qui dormait en file part maintenant, sans attendre
    // l'événement `online` — qui n'arrive pas quand la coupure était côté
    // serveur plutôt que côté appareil.
    if (etatDeLaFile().enAttente > 0) void vider()

    return resultat
  } catch (erreur) {
    if (erreur instanceof ApiError) throw erreur

    await mettreEnFile(lot, contexte)

    return {
      accepted: lot.map((r) => r.client_uuid),
      rejected: {},
      states: {},
      reveals: {},
    }
  }
}

/**
 * L'identifiant d'une réponse, généré AVANT l'envoi.
 *
 * C'est lui qui porte l'idempotence : le candidat qui répond en mode avion et
 * se reconnecte dans un tap-tap enverra son lot plusieurs fois (CA-9.2).
 * `crypto.randomUUID` n'existe pas sur les navigateurs les plus anciens du
 * parc visé — le repli n'a pas besoin d'être cryptographique, seulement unique.
 */
export function nouvelIdentifiant(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()

  const octets = crypto.getRandomValues(new Uint8Array(16))

  octets[6] = ((octets[6] ?? 0) & 0x0f) | 0x40
  octets[8] = ((octets[8] ?? 0) & 0x3f) | 0x80

  const hex = [...octets].map((o) => o.toString(16).padStart(2, '0')).join('')

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}
